import { Autocomplete, Box, FormControl, InputLabel, TextField } from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useCookies } from "react-cookie";
import { customerDefault } from "../../../../consts/consts";
import customerFindAllListResponse from "../../../../resources/Api/customer/customerFindAllListResponse";
import { findCustomerFeatures } from "../../../../resources/Api/customerParameter/customerFeatures";
import { CustomerListType } from "../../../../types/types";
import { WorkScheduleContent } from "../../../privateApp/workSchedules";

/**
 * WorkScheduleComponent - Seleciona o cliente no atendimento do manager e
 * reutiliza o mesmo cadastro de escalas exibido para o cliente.
 * @component
 */
const WorkScheduleComponent = () => {
    const [cookies] = useCookies(["token", "selectedParameters"]);
    const [customersList, setCustomersList] = useState<CustomerListType[]>([]);
    const [customer, setCustomer] = useState<CustomerListType>(
        customerDefault as unknown as CustomerListType,
    );
    const [featureState, setFeatureState] = useState<{ customerId: number; enabled: boolean; error: boolean } | null>(null);

    const managerId = cookies.selectedParameters?.customer?.id ?? -1;

    const getCustomersList = useCallback(async () => {
        if (managerId <= 0) {
            setCustomersList([]);
            return;
        }

        const token = cookies.token || sessionStorage.getItem("token");
        try {
            const response = await customerFindAllListResponse(token, managerId);
            setCustomersList(response.data);
        } catch (error) {
            console.log(error);
            setCustomersList([]);
        }
    }, [cookies.token, managerId]);

    useEffect(() => {
        getCustomersList();
    }, [getCustomersList]);

    useEffect(() => {
        if (customer.id <= 0) {
            setFeatureState(null);
            return;
        }
        let active = true;
        const customerId = customer.id;
        const token = cookies.token || sessionStorage.getItem("token") || "";
        setFeatureState(null);
        findCustomerFeatures(token, customerId)
            .then((features) => {
                if (features.customerId !== customerId) throw new Error("Cliente divergente nos parâmetros.");
                if (active) setFeatureState({ customerId, enabled: features.useWorkSchedules, error: false });
            })
            .catch(() => {
                if (active) setFeatureState({ customerId, enabled: false, error: true });
            });
        return () => { active = false; };
    }, [customer.id, cookies.token]);

    const workSchedulesEnabled = featureState?.customerId === customer.id && featureState.enabled && !featureState.error;
    const featureMessage = customer.id <= 0
        ? "Selecione um cliente para consultar as escalas de trabalho."
        : featureState?.customerId !== customer.id
            ? "Verificando os parâmetros do cliente..."
            : featureState.error
                ? "Não foi possível verificar os parâmetros do cliente. Selecione-o novamente para tentar outra vez."
                : !featureState.enabled
                    ? "Este cliente não utiliza escalas de trabalho. A criação e a edição estão desabilitadas."
                    : "";

    const customerParameter = useMemo(
        () => (
            <Box
                className="tw-w-full"
                sx={{
                    width: "100%",
                    minWidth: 0,
                }}
            >
                <Autocomplete
                    id="chip-label-select-customer"
                    className="input-select-customer"
                    style={{
                        width: "100%",
                        minHeight: "56px",
                        height: "56px",
                    }}
                    options={customersList}
                    value={customer}
                    onChange={(_, option) => {
                        setCustomer(option ?? (customerDefault as unknown as CustomerListType));
                    }}
                    getOptionLabel={(option) => `${option.reducedName || option.name}`}
                    noOptionsText="Nada encontrado"
                    renderInput={(params) => (
                        <FormControl className="form-group" style={{ display: "flex" }}>
                            <InputLabel required sx={{ fontFamily: "Prometo" }} shrink>
                                Cliente
                            </InputLabel>
                            <TextField
                                {...params}
                                fullWidth
                                variant="outlined"
                                placeholder="Selecione um cliente"
                                autoComplete="off"
                                required
                                InputLabelProps={{ shrink: true }}
                                InputProps={{
                                    ...params.InputProps,
                                    notched: true,
                                    label: "Cliente",
                                    classes: { notchedOutline: "custom-notch" },
                                }}
                            />
                        </FormControl>
                    )}
                />
            </Box>
        ),
        [customer, customersList],
    );

    return (
        <WorkScheduleContent
            key={customer.id}
            customerId={customer.id}
            isHiddenMenu={false}
            canCreate={workSchedulesEnabled}
            canEdit={workSchedulesEnabled}
            canDelete={workSchedulesEnabled}
            disableOptionsButton={!workSchedulesEnabled}
            optionsDisabledReason={featureMessage}
            detailsTitle={
                customer.id > 0
                    ? `Escala de trabalho — ${customer.reducedName || customer.name}`
                    : "Escala de trabalho"
            }
            customerParameter={customerParameter}
            contentHeight="500px"
            containerHeight="auto"
            gridStyle={{ width: "100%", marginTop: 0, padding: 0 }}
        />
    );
};

export default WorkScheduleComponent;
